// Phase 1G -- Evidence: for trust, not another dashboard.
//   WHAT MARTIAN KNOWS -> WHAT IT OBSERVED -> WHERE IT CAME FROM -> WHEN
// Raw technical detail stays one level deeper (the existing AI research data
// page) -- never on this first screen.
import { useEffect, useState } from 'react';
import { companyContextAPI, somHistoryAPI } from '@/lib/api';
import { checkState, formatMeasurementState, usablePromptCount } from '@/lib/somHistoryHelpers';
import { connectorsAPI } from '@/lib/intelligenceApi';
import { useFounderWorkspace, useFounderPath } from '../FounderWorkspace';
import { EmptyState, Loading, PageHeader, PrimaryButton, Section, SecondaryButton, TextButton, Stat, formatDate } from '../ui';

const FIELDS = ['company_name', 'business_summary', 'audience', 'primary_market', 'markets_served', 'positioning', 'growth_priorities', 'additional_context'];

function factCounts(ctx) {
  const out = { confirmed: 0, inferred: 0, missing: 0, inferredAt: null };
  FIELDS.forEach((k) => {
    const f = ctx?.[k];
    const empty = !f || f.value === null || f.value === undefined || f.value === '' || (Array.isArray(f.value) && !f.value.length);
    if (empty) out.missing += 1;
    else if (f.source === 'user') out.confirmed += 1;
    else { out.inferred += 1; if (!out.inferredAt || (f.updated_at || '') > out.inferredAt) out.inferredAt = f.updated_at; }
  });
  return out;
}

export default function EvidencePage() {
  const { workspaceId, summary, displayName, loadingClients } = useFounderWorkspace();
  const fp = useFounderPath();
  const [ctx, setCtx] = useState(null);
  const [checks, setChecks] = useState(null);
  const [connectors, setConnectors] = useState(null);

  useEffect(() => {
    if (!workspaceId) return;
    setCtx(null); setChecks(null); setConnectors(null);
    companyContextAPI.get(workspaceId).then((r) => setCtx(r.data)).catch(() => setCtx({}));
    somHistoryAPI.production(workspaceId).then((r) => setChecks(r.data || [])).catch(() => setChecks([]));
    connectorsAPI.list(workspaceId).then((r) => setConnectors(r.data?.connectors || r.data || [])).catch(() => setConnectors([]));
  }, [workspaceId]);

  if (loadingClients || (workspaceId && (!ctx || checks === null || connectors === null || !summary))) return <Loading />;
  // No workspace yet: ctx/checks/connectors only ever load for a workspace, so
  // they are still null here. Say there is no evidence (same pattern as Your
  // Business) instead of reading checks[0] below. A workspace whose requests
  // failed or returned nothing is a different state and renders as before
  // ("None yet" / "None connected yet").
  if (!workspaceId) {
    return (
      <div data-testid="evidence-page">
        <EmptyState testId="evidence-no-workspace" title="No evidence yet"
          body="Evidence appears once Martian has read your website and run its first check. Add your website to begin."
          action={<PrimaryButton to="/onboarding" testId="evidence-start-onboarding">Add your website</PrimaryButton>} />
      </div>
    );
  }
  const facts = factCounts(ctx);
  const lead = (summary?.decisions || []).find((d) => d.domain === 'ai_search');
  const sources = lead?.evidence?.sources;
  const latest = checks[0];
  const brand = displayName || 'your brand';
  const liveConnectors = (Array.isArray(connectors) ? connectors : []).filter((c) => (c.status || '').toUpperCase() !== 'DISCONNECTED');

  return (
    <div data-testid="evidence-page">
      <PageHeader
        eyebrow="Evidence"
        title={`Everything Martian knows about ${brand}`}
        summary="What Martian knows, what it observed, where it came from and when. Every decision links back here."
        secondary={<SecondaryButton to={fp('/evidence/connections')} testId="evidence-connections">Connections</SecondaryButton>}
      />

      <Section eyebrow="What Martian knows" testId="evidence-knows">
        <div className="grid grid-cols-3 gap-6 max-w-[560px]">
          <Stat value={facts.confirmed} label="facts you confirmed" tone={facts.confirmed ? 'good' : undefined} />
          <Stat value={facts.inferred} label="facts read from your website" />
          <Stat value={facts.missing} label="facts still missing" />
        </div>
        <div className="mt-4"><TextButton to={fp('/business')} tone="accent">Review your business</TextButton></div>
      </Section>

      <Section eyebrow="What it observed" testId="evidence-observed">
        {latest ? (
          <>
            <p className="text-[15px] max-w-[62ch]">
              {checkState(latest) === 'OBSERVED'
                ? <>Latest check {formatDate(latest.created_at, true)}: {brand} appeared in {latest.mentions ?? 0} of {usablePromptCount(latest) ?? 0} AI answers</>
                : <>Latest check {formatDate(latest.created_at, true)}: {formatMeasurementState(latest).toLowerCase()} — too few of the questions got an answer to say whether {brand} appears</>}
              {sources?.total ? `, which cited ${sources.total} pages across ${sources.top_domains?.length ? `${sources.top_domains.length}+ sites` : 'several sites'}` : ''}.
            </p>
            {lead && <div className="mt-3"><TextButton to={fp(`/decisions/${encodeURIComponent(lead.id)}#evidence`)}>Read the answers</TextButton></div>}
          </>
        ) : <p className="text-[14.5px] text-[var(--text-secondary)]">Martian hasn't completed a check yet.</p>}
      </Section>

      <Section eyebrow="Where it came from" testId="evidence-sources">
        <ul className="divide-y divide-[var(--border)]">
          <li className="py-3 flex flex-col sm:flex-row sm:items-baseline justify-between gap-1">
            <span className="text-[14.5px] font-medium">Your website</span>
            <span className="text-[13px] text-[var(--text-secondary)]">{facts.inferred ? `${facts.inferred} facts read · ${formatDate(facts.inferredAt)}` : 'Nothing read yet'}</span>
          </li>
          <li className="py-3 flex flex-col sm:flex-row sm:items-baseline justify-between gap-1">
            <span className="text-[14.5px] font-medium">You</span>
            <span className="text-[13px] text-[var(--text-secondary)]">{facts.confirmed ? `${facts.confirmed} facts confirmed or corrected` : 'Nothing confirmed yet'}</span>
          </li>
          <li className="py-3 flex flex-col sm:flex-row sm:items-baseline justify-between gap-1">
            <span className="text-[14.5px] font-medium">AI search checks</span>
            <span className="text-[13px] text-[var(--text-secondary)]">{checks.length ? `${checks.length} check${checks.length === 1 ? '' : 's'} · latest ${formatDate(latest.created_at)}` : 'None yet'}</span>
          </li>
          {sources?.top_domains?.length > 0 && (
            <li className="py-3">
              <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1">
                <span className="text-[14.5px] font-medium">Pages AI cited</span>
                <span className="text-[13px] text-[var(--text-secondary)]">{sources.total} pages · {sources.mention_brand} mention {brand}</span>
              </div>
              <p className="mt-1.5 text-[12.5px] text-[var(--text-tertiary)]">Most cited: {sources.top_domains.map((d) => d.domain).join(', ')}</p>
            </li>
          )}
          <li className="py-3 flex flex-col sm:flex-row sm:items-baseline justify-between gap-1">
            <span className="text-[14.5px] font-medium">Connected data</span>
            <span className="text-[13px] text-[var(--text-secondary)]">{liveConnectors.length ? liveConnectors.map((c) => c.display_name || c.provider).join(', ') : 'None connected yet'}</span>
          </li>
          {liveConnectors.some((c) => c.provider === 'google_search_console') && (
            <li className="py-3">
              <TextButton to={fp('/search-demand')} testId="evidence-search-demand">Search Console performance</TextButton>
            </li>
          )}
          {liveConnectors.some((c) => c.provider === 'google_analytics') && (
            <li className="py-3">
              <TextButton to={fp('/analytics')} testId="evidence-analytics">Analytics performance</TextButton>
            </li>
          )}
          {liveConnectors.some((c) => c.provider === 'shopify') && (
            <li className="py-3">
              <TextButton to={fp('/growth/ecommerce')} testId="evidence-ecommerce">Shopify orders</TextButton>
            </li>
          )}
        </ul>
      </Section>

      <Section eyebrow="Deeper detail" testId="evidence-deeper">
        <p className="text-[14px] text-[var(--text-secondary)] max-w-[62ch]">Every raw AI answer and citation Martian has recorded, for when you want to check its working.</p>
        <div className="mt-3"><TextButton to="/ai-discovery/raw-data">Open AI research data</TextButton></div>
      </Section>
    </div>
  );
}
